import React from 'react';
import { Icon } from 'antd';

/**
 * O player das Novidades.
 *
 * `<video controls>` nativo não serve: cada navegador desenha o seu, nenhum
 * oferece 1,25x — que num vídeo de release é o controle mais usado — e nenhum
 * aceita os capítulos, que aqui saem do roteiro e não de marcação a olho.
 *
 * Sem botão de legenda por enquanto, de propósito: os vídeos ainda não sobem
 * com faixa VTT, e um controle que não liga nada é pior que a ausência dele.
 */
var VELOCIDADES = [1, 1.25, 1.5, 2];
var ROXO = '#4B2183';

// Com mais de um vídeo na tela, só um responde ao teclado e só um toca por
// vez: o último usado. Sem isso o espaço dava play nos dois ao mesmo tempo.
var montados = [];
var ativo = null;
function ehDoTeclado(src) {
  return ativo ? ativo === src : montados[0] === src;
}

function mmss(s) {
  if (!isFinite(s) || s < 0) return '0:00';
  var m = Math.floor(s / 60), r = Math.floor(s % 60);
  return m + ':' + (r < 10 ? '0' : '') + r;
}

export default React.forwardRef(function Player(props, refExterna) {
  var src = props.src;
  var poster = props.poster;
  var capitulos = props.capitulos || [];
  var comecarEm = props.comecarEm || 0;
  var onTempo = props.onTempo;
  var compacto = !!props.compacto;
  var ref = React.useRef(null);
  var estado = React.useState(false); var tocando = estado[0], setTocando = estado[1];
  var tRef = React.useState(0); var atual = tRef[0], setAtual = tRef[1];
  var dRef = React.useState(0); var total = dRef[0], setTotal = dRef[1];
  var vRef = React.useState(0); var iVel = vRef[0], setIVel = vRef[1];
  var mRef = React.useState(false); var mudo = mRef[0], setMudo = mRef[1];

  React.useImperativeHandle(refExterna, function() {
    return { irPara: function(seg) { var v = ref.current; if (!v) return; v.currentTime = seg; v.play(); } };
  }, []);

  // Trocar de vídeo tem de zerar o relógio: sem isso a barra continua na
  // posição do anterior até o primeiro timeupdate chegar.
  React.useEffect(function() {
    setAtual(0); setTotal(0); setTocando(false);
  }, [src]);

  var alternar = React.useCallback(function() {
    var v = ref.current; if (!v) return;
    if (v.paused) { v.play(); } else { v.pause(); }
  }, []);

  React.useEffect(function() {
    montados.push(src);
    function outroTocou(e) {
      var v = ref.current;
      if (v && e.detail !== src && !v.paused) v.pause();
    }
    window.addEventListener('novidades:tocou', outroTocou);
    return function() {
      montados = montados.filter(function(m) { return m !== src; });
      if (ativo === src) ativo = null;
      window.removeEventListener('novidades:tocou', outroTocou);
    };
  }, [src]);

  React.useEffect(function() {
    function tecla(e) {
      if (e.target && /INPUT|TEXTAREA/.test(e.target.tagName)) return;
      if (!ehDoTeclado(src)) return;
      var v = ref.current; if (!v) return;
      if (e.code === 'Space') { e.preventDefault(); alternar(); }
      if (e.code === 'ArrowRight') v.currentTime = Math.min(v.duration || 0, v.currentTime + 5);
      if (e.code === 'ArrowLeft') v.currentTime = Math.max(0, v.currentTime - 5);
    }
    document.addEventListener('keydown', tecla);
    return function() { document.removeEventListener('keydown', tecla); };
  }, [alternar, src]);

  var pct = total ? (atual / total) * 100 : 0;

  return (
    <div>
      <div
        className="novidades-palco"
        style={{
          position: 'relative', borderRadius: compacto ? 12 : 14, overflow: 'hidden',
          background: '#0C0618', border: '1px solid rgba(255,255,255,.14)',
          boxShadow: compacto ? '0 14px 34px -18px rgba(0,0,0,.5)' : '0 24px 60px -22px rgba(0,0,0,.55)',
        }}
      >
        <video
          ref={ref}
          src={src}
          poster={poster}
          playsInline
          preload="metadata"
          onClick={alternar}
          onPlay={function() {
            setTocando(true);
            ativo = src;
            try { window.dispatchEvent(new CustomEvent('novidades:tocou', { detail: src })); } catch (e) {}
          }}
          onPause={function() { setTocando(false); }}
          onEnded={function() { setTocando(false); }}
          onLoadedMetadata={function(e) {
            setTotal(e.target.duration || 0);
            // retoma onde parou; comecarEm só vem preenchido acima de 3s, para
            // não "retomar" alguém que mal apertou o play
            if (comecarEm && comecarEm < (e.target.duration || 0) - 2) {
              e.target.currentTime = comecarEm;
            }
          }}
          onTimeUpdate={function(e) {
            setAtual(e.target.currentTime);
            if (onTempo) onTempo(e.target.currentTime);
          }}
          style={{ display: 'block', width: '100%', aspectRatio: '16/9', background: '#0C0618', cursor: 'pointer' }}
        />

        {!tocando && (
          <button
            onClick={alternar}
            aria-label="Reproduzir"
            style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', background: 'rgba(21,11,43,.40)', border: 0, cursor: 'pointer',
            }}
          >
            <span style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: compacto ? 52 : 72, height: compacto ? 52 : 72, borderRadius: '50%', background: ROXO, color: '#fff',
              boxShadow: '0 14px 40px -10px rgba(75,33,131,.9)',
            }}>
              <Icon type="caret-right" style={{ fontSize: compacto ? 22 : 30, marginLeft: compacto ? 3 : 4 }} />
            </span>
          </button>
        )}

        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, padding: compacto ? '16px 12px 9px' : '20px 16px 12px',
          background: 'linear-gradient(to top, rgba(8,4,18,.94), rgba(8,4,18,.66) 55%, transparent)',
        }}>
          <div style={{ position: 'relative', height: 6 }}>
            <div style={{ position: 'absolute', inset: 0, borderRadius: 99, background: 'rgba(255,255,255,.22)' }} />
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: pct + '%', borderRadius: 99, background: '#fff' }} />
            {/* Um risco por capítulo. Só faz sentido depois que a duração
                chega — antes dela, a posição seria dividida por zero. */}
            {total > 0 && capitulos.map(function(c, i) {
              if (i === 0) return null;
              return (
                <div key={i} title={c.rotulo} style={{
                  position: 'absolute', top: -1, width: 3, height: 8,
                  left: 'calc(' + ((c.t / total) * 100) + '% - 1.5px)',
                  borderRadius: 2, background: 'rgba(8,4,18,.6)', pointerEvents: 'none',
                }} />
              );
            })}
            <input
              type="range" min={0} max={1000} value={total ? Math.round((atual / total) * 1000) : 0}
              onChange={function(e) { var v = ref.current; if (v && total) v.currentTime = (e.target.value / 1000) * total; }}
              aria-label="Posição"
              style={{ position: 'absolute', inset: '-5px 0', width: '100%', opacity: 0, cursor: 'pointer', margin: 0 }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: compacto ? 10 : 14, marginTop: compacto ? 9 : 12, color: '#fff' }}>
            <Icon
              type={tocando ? 'pause' : 'caret-right'}
              onClick={alternar}
              style={{ fontSize: 18, cursor: 'pointer' }}
            />
            <span style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: 'rgba(255,255,255,.82)' }}>
              {mmss(atual)} / {mmss(total)}
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
              <span
                onClick={function() {
                  var n = (iVel + 1) % VELOCIDADES.length;
                  setIVel(n);
                  if (ref.current) ref.current.playbackRate = VELOCIDADES[n];
                }}
                style={{ fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}
              >
                {String(VELOCIDADES[iVel]).replace('.', ',')}×
              </span>
              <Icon
                type={mudo ? 'sound' : 'notification'}
                onClick={function() { var v = ref.current; if (!v) return; v.muted = !v.muted; setMudo(v.muted); }}
                style={{ fontSize: 17, cursor: 'pointer', opacity: mudo ? .5 : 1 }}
              />
              <Icon
                type="fullscreen"
                onClick={function() {
                  var el = ref.current && ref.current.parentNode;
                  if (document.fullscreenElement) { document.exitFullscreen(); }
                  else if (el && el.requestFullscreen) { el.requestFullscreen(); }
                }}
                style={{ fontSize: 17, cursor: 'pointer' }}
              />
            </span>
          </div>
        </div>
      </div>

    </div>
  );
});
